<script>
import { useRoute } from 'vue-router';
import GenericLoader from 'vue-elements/comparison-elements/GenericLoader.vue';
import WizardStepMixin from '../../../../vue-elements/wizard/lib/WizardStepMixin';
import StepInfoToggle from '../StepInfoToggle.vue';
import FlowPart from '../FlowPart.vue';
import TextInput from '../../../../vue-elements/input/TextInput.vue';
import { useEnergyComparisonStore } from '../../stores/energyComparison';
import ButtonIconRight from '../../../../../abstracts/elements/button/icon-right/button-icon-right.vue';
import FieldError from '../../../../vue-elements/field/FieldError.vue';
import LinkIconRight from '../../../../../abstracts/elements/link/icon-right/link-icon-right.vue';

export default {
  name: 'PostalStep',

  components: {
    LinkIconRight,
    GenericLoader,
    FieldError,
    ButtonIconRight,
    TextInput,
    FlowPart,
    StepInfoToggle,
  },
  mixins: [WizardStepMixin()],
  setup() {
    const comparisonStore = useEnergyComparisonStore();
    const route = useRoute();

    return {
      comparisonStore,
      route,
    };
  },
  data() {
    return {
      postalCode: '',
      houseNumber: '',
      modifyAddress: false,
      postalError: null,
      loading: false,
    };
  },
  watch: {
    postalError() {
      document.getElementById('zip-code')
        ?.setCustomValidity(this.postalError || '');
      document.getElementById('house-number')
        ?.setCustomValidity(this.postalError || '');
    },
    'comparisonStore.locationLoading': {
      handler() {
        this.loading = this.comparisonStore.locationLoading;
      },
    },
    'comparisonStore.error': {
      handler(error) {
        if (error && (!this.postalCode || !this.houseNumber)) {
          this.postalError = 'Vul je postcode en huisnummer in om verder te gaan.';
        } else if (error) {
          this.houseNumber = this.comparisonStore.houseNumber;
          if (this.comparisonStore.houseNumberAddition) {
            this.houseNumber += ` ${this.comparisonStore.houseNumberAddition}`;
          }
          this.postalCode = this.comparisonStore.postalCode;
          this.postalError = 'Deze postcode is niet bekend bij ons. Controleer of je de juiste postcode hebt ingevuld.';
        }
      },
      immediate: true,
    },
    'comparisonStore.postalCode': {
      handler() {
        this.postalCode = this.comparisonStore.postalCode;
        this.houseNumber = this.comparisonStore.houseNumber;

        if (this.comparisonStore.houseNumberAddition != null && this.comparisonStore.houseNumberAddition.length > 0) {
          this.houseNumber += ` ${this.comparisonStore.houseNumberAddition}`;
        }
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.route.query.step === 'PostalStep') {
      this.modifyAddress = true;
    }
  },
  methods: {
    findLocation() {
      this.loading = true;
      this.postalError = null;
      this.comparisonStore.loadPostalLocation(this.postalCode, this.houseNumber, null)
        .then((response) => {
          if (response) {
            this.nextStep();
            this.logAnalyticsEvent('compare-energy.wizard-step-complete', {
              step: this.$options.name,
              value: `${this.postalCode} ${this.houseNumber}`,
            });
            this.modifyAddress = false;
            if (this.comparisonStore.filters.step === 'PostalStep') {
              delete this.comparisonStore.filters.step;
            }
          } else {
            this.postalError = 'Deze postcode is niet bekend bij ons. Controleer of je de juiste postcode hebt ingevuld.';
          }
        })
        .catch(() => {
          this.postalError = 'Deze postcode is niet bekend bij ons. Controleer of je de juiste postcode hebt ingevuld.';
        })
        .finally(() => {
          this.loading = false;
        });
    },

  },
};
</script>
<template>
  <flow-part :id="$options.name" class="postal-step no-height">
    <template #content>
      <div v-auto-animate>
        <div v-if="comparisonStore.compareLocation && comparisonStore.locationFound && !modifyAddress"
          class="location-box">
          <div class="adress-field">
            {{ comparisonStore.compareLocation.street }}
            {{ comparisonStore.compareLocation.number }}
            {{ comparisonStore.compareLocation.extension }}
            <br>
            {{ comparisonStore.compareLocation.postal_code }}
            {{ comparisonStore.compareLocation.city }}
          </div>

          <div>
            <link-icon-right label="Wijzig adres" icon="x-fa-edit" @click="modifyAddress = true" />
          </div>
        </div>

        <div v-else>
          <div class="input-fields postal-box">
            <TextInput id="zip-code" ref="zipCodeField" v-model="postalCode" label="Postcode" placeholder=""
              :disabled="comparisonStore.locationLoading" @input="postalError = null" />
            <TextInput id="house-number" ref="numberField" v-model="houseNumber" label="Huisnummer" placeholder=""
              :disabled="comparisonStore.locationLoading" @input="postalError = null" @keyup.enter="findLocation()" />
            <FieldError :error="postalError" />

            <ButtonIconRight type="cta" :disabled="comparisonStore.locationLoading" :class="loading ? 'loading' : ''"
              :icon="loading
                ?
                'x-fa-spinner'
                :
                'arrow-right-long-solid'" label="Ok" @click="findLocation()" />
          </div>
          <step-info-toggle title="Waar is mijn postcode voor nodig?">
            <template #content>
              We hebben je postcode en huisnummer nodig om de correcte
              energieprijzen voor jouw woning uit te rekenen.
            </template>
          </step-info-toggle>
        </div>
      </div>
      <GenericLoader v-if="loading" type="blur" />
    </template>
  </flow-part>
</template>

<style lang="scss">
@use 'assets/scss/all' as *;

.energy-flow-view .energy-flow-parts-container .flow-part-container:nth-of-type(odd) {
  &.postal-step.no-height {
    @include breakpoint-down(xl) {
      background: $grey-10;
      border-bottom: 1px solid $border-base;
      padding: 24px;
    }

    @include breakpoint-down(sm) {
      padding: 16px;
    }
  }
}

.postal-step {
  .location-box {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    color: $text-subtle;
    align-items: center;

    @include breakpoint-up(xl) {
      padding: 12px 16px;
      border: 1px solid $border-base;
      background: $grey-10;
      border-radius: 15px;
      margin-bottom: 16px;
    }
  }

  .input-fields.postal-box {
    margin-bottom: 16px;
    display: grid;
    gap: 16px;
    grid-template-areas:
      'zipcode number'
      'error error'
      'button button';

    align-items: flex-end;

    @include breakpoint-up(md) {
      grid-template-areas: 'zipcode number button'
        'error error error';
      grid-template-columns: auto auto 1fr;
    }
  }

  .step-info {
    margin-bottom: 16px;
  }

  .toggle-content {
    background-color: $background-default;
  }
  .button-element {
    grid-area: button;

    &.loading {
      .icon-element {
        transform: rotate(720deg);
        transition: transform 4s ease;
      }
    }
  }

  .field-error {
    grid-area: error;
  }
}
</style>
